/**
 * Paywall constants + the pre-paint auth hint. Shared by the gate UI, the
 * paywalled-content JSON-LD and the tests, so the CSS class Google's
 * `cssSelector` points at can never drift from the markup.
 */

/** Class on the container that holds the gated (Pro-only) part of a lesson. */
export const PRO_GATED_CLASS = 'pro-gated'

/** CSS selector for {@link PRO_GATED_CLASS}, as emitted in JSON-LD `hasPart`. */
export const PRO_GATED_SELECTOR = `.${PRO_GATED_CLASS}`

/** Anchor id of the gated container (ToC links to locked sections jump here). */
export const PRO_GATED_ID = 'pro-content'

/**
 * Whether a `document.cookie` string carries a live Clerk session hint.
 *
 * Clerk's browser SDK keeps a first-party, non-httpOnly `__client_uat` cookie
 * (optionally suffixed per instance) holding the last sign-in timestamp, or `0`
 * when signed out. It proves NOTHING about identity or entitlement and is never
 * used for an access decision; it only lets the lesson page swap the paywall
 * card for a skeleton before first paint, so signed-in (often Pro) readers do
 * not see a flash of the paywall while Clerk + Convex confirm the session.
 *
 * The same pattern runs in the pre-paint bootstrap below as a FIXED source
 * string: never `Function.prototype.toString`, whose output differs between
 * the SSR and the client bundle and broke hydration of the inline script.
 */
const CLERK_UAT_PATTERN = '(?:^|;\\s*)__client_uat(?:_[A-Za-z0-9-]+)?=([^;]*)'

export function hasClerkSessionHint(cookie: string): boolean {
  const pattern = new RegExp(CLERK_UAT_PATTERN, 'g')
  let match = pattern.exec(cookie)
  while (match) {
    const value = match[1]
    if (value && value !== '0') return true
    match = pattern.exec(cookie)
  }
  return false
}

/** `<html data-auth-hint="1">` while a Clerk session hint is present. */
export const AUTH_HINT_DATASET_KEY = 'authHint'

/**
 * Inline, pre-paint bootstrap (same pattern as the theme bootstrap in
 * `__root.tsx`). Sets `<html data-auth-hint="1">` for signed-in visitors; CSS
 * uses it to show the Pro skeleton instead of the paywall during hydration.
 */
export const authHintBootstrap = `(() => {
  try {
    var pattern = new RegExp(${JSON.stringify(CLERK_UAT_PATTERN)}, 'g');
    var match = pattern.exec(document.cookie);
    while (match) {
      if (match[1] && match[1] !== '0') {
        document.documentElement.dataset.${AUTH_HINT_DATASET_KEY} = '1';
        return;
      }
      match = pattern.exec(document.cookie);
    }
  } catch (e) {}
})();`

/**
 * `<html data-billing="live|early">`: the last known BILLING_LIVE state of
 * this browser (written by `useBillingStatus`). Like the auth hint it decides
 * NOTHING about access; it only picks the right Pro-lesson placeholder before
 * hydration: in early access a skeleton (the content is about to appear) and
 * when live the gate. Unknown (first visit, crawlers) keeps the gate.
 */
export const BILLING_HINT_DATASET_KEY = 'billing'
export const BILLING_HINT_STORAGE_KEY = 'as_billing_hint'

export const billingHintBootstrap = `(() => {
  try {
    var hint = window.localStorage.getItem('${BILLING_HINT_STORAGE_KEY}');
    if (hint === 'live' || hint === 'early') {
      document.documentElement.dataset.${BILLING_HINT_DATASET_KEY} = hint;
    }
  } catch (e) {}
})();`
